import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["buttonStyles", "buttonSize"],
  _excluded2 = ["children", "showChildrenForRequestApproval"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Children, useContext } from 'react';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useCollaborationSidebarHasUnrecoverableError } from 'collaboration-sidebar/public/hooks/useCollaborationSidebarHasUnrecoverableError';
import { useCollaborationSidebarOpenState } from 'collaboration-sidebar/public/hooks/useCollaborationSidebarOpenState';
import { ApprovalContext } from '../../context/approvalContext';
import { useApprovalConditions } from '../../hooks/useApprovalConditions';
import { useApprovalContext } from '../../hooks/useApprovalContext';
import { useApprovalEditorButtonsOutcome } from '../../hooks/useApprovalEditorButtonsOutcome';
import { useHasApprovalScope } from '../../hooks/useHasApprovalScope';
import { ApprovalOperation } from 'ui-product-approvals-common';
import { ErrorBoundary } from '../error/ErrorBoundary';
import { ApprovalButton } from '../button/ApprovalButton';
import { LegacyRequestChangesCommentButton } from '../button/LegacyRequestChangesCommentButton';
import { useHasLegacyRequestChangesCommentButton } from '../../hooks/useHasLegacyRequestChangesCommentButton';
import { useHasSeqReqChangesRedesign } from '../../hooks/useHasSeqReqChangesRedesign';
import { ApprovalEditorButtonSkeleton, getButtonTokens } from './approvalEditorButtonSkeleton';
import { ApprovalEditorErrorButton } from './approvalEditorErrorButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const MAIN_OPERATION_PRIORITY = [ApprovalOperation.REQUEST, ApprovalOperation.APPROVE];
const DropdownIconButton = buttonProps => /*#__PURE__*/_jsx(IconButton, Object.assign({
  "aria-label": unescapedText('productApprovals.ButtonCollapser.moreActions')
}, buttonProps, {
  children: /*#__PURE__*/_jsx(DownIcon, {})
}));
const ApprovalEditorButtonGroup = ({
  additionalDropdownButtons,
  approvalOperationDisabledReason,
  dropdownProps,
  size = 'medium'
}) => {
  var _MAIN_OPERATION_PRIOR;
  const approval = useApprovalContext();
  const hasRedesign = useHasSeqReqChangesRedesign(approval.meta.approvalType);
  const {
    isApprovalActive,
    isCancelled
  } = useApprovalConditions();
  const {
    openApprovals,
    openComments
  } = useCollaborationSidebarOpenState();
  const {
    hasUnrecoverableError
  } = useCollaborationSidebarHasUnrecoverableError();
  const hasWritePermission = useHasApprovalScope(approval.meta.approvalType, 'write');
  const {
    availableOperations
  } = approval.meta;
  const hasLegacyRequestChangesCommentButton = useHasLegacyRequestChangesCommentButton({
    hasLegacyCommentDestination: true
  });
  const shouldShowLegacyRequestChangesCommentButton = !hasRedesign && hasLegacyRequestChangesCommentButton;

  // Under the redesign the operation buttons move to the sidebar
  // (per approver row + footer Cancel), so the editor only offers "Request
  // approval" (nothing to view before an approval exists) or "View approval".
  const mainOperation = hasRedesign ? availableOperations.includes(ApprovalOperation.REQUEST) ? ApprovalOperation.REQUEST : null : (_MAIN_OPERATION_PRIOR = MAIN_OPERATION_PRIORITY.find(op => availableOperations.includes(op))) !== null && _MAIN_OPERATION_PRIOR !== void 0 ? _MAIN_OPERATION_PRIOR : null;
  const isViewApprovalMain = !mainOperation && isApprovalActive;
  if (!mainOperation && !isViewApprovalMain) {
    return null;
  }
  const hasDisabledReason = approvalOperationDisabledReason !== undefined && approvalOperationDisabledReason !== null;
  const menuOperations = hasRedesign ? [] : availableOperations.filter(op => op !== mainOperation);
  const showViewApprovalInMenu = Boolean(mainOperation) && (isApprovalActive || isCancelled);
  const additionalDropdownButtonItems = Children.toArray(additionalDropdownButtons).filter(button => typeof button !== 'boolean').map((button, index) => /*#__PURE__*/_jsx(ListItem, {
    children: button
  }, `additional-dropdown-button-${index}`));
  const hasMenuItems = menuOperations.length > 0 || shouldShowLegacyRequestChangesCommentButton || showViewApprovalInMenu || additionalDropdownButtonItems.length > 0;
  const _ref = dropdownProps !== null && dropdownProps !== void 0 ? dropdownProps : {},
    {
      buttonStyles: dropdownButtonStyles,
      buttonSize: callerDropdownButtonSize
    } = _ref,
    otherDropdownProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    iconButtonSize,
    maxBlockSize,
    paddingBlock
  } = getButtonTokens(size);
  const renderViewApproval = () => /*#__PURE__*/_jsx(Tooltip, {
    disabled: !hasUnrecoverableError,
    placement: "left",
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "productApprovals.ApprovalEditorButtons.approvalsUnavailableTooltip"
    }),
    children: /*#__PURE__*/_jsx(Button, {
      "data-test-id": "editor-approval-view-approval-button",
      disabled: hasUnrecoverableError,
      onClick: openApprovals,
      size: size,
      use: "primary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "productApprovals.ApprovalEditorButtons.viewApproval"
      })
    })
  });
  return /*#__PURE__*/_jsxs(ButtonGroup, {
    display: "flex",
    children: [mainOperation ? /*#__PURE__*/_jsx(ApprovalButton, {
      availableOperation: mainOperation,
      disabled: !hasWritePermission || hasDisabledReason,
      disabledReason: approvalOperationDisabledReason,
      size: size
    }) : renderViewApproval(), hasMenuItems && /*#__PURE__*/_jsx(Dropdown, Object.assign({
      "data-test-id": "editor-approval-buttons-dropdown",
      Button: DropdownIconButton,
      buttonSize: callerDropdownButtonSize !== null && callerDropdownButtonSize !== void 0 ? callerDropdownButtonSize : iconButtonSize,
      buttonStyles: Object.assign({
        borderInlineStartColor: 'fill-accent-orange-hover',
        borderInlineStartWidth: '100',
        borderInlineStartStyle: 'solid',
        paddingBlock,
        paddingInline: '100',
        maxBlockSize
      }, dropdownButtonStyles),
      buttonUse: "primary",
      caretRenderer: null
    }, otherDropdownProps, {
      children: /*#__PURE__*/_jsxs(List, {
        children: [shouldShowLegacyRequestChangesCommentButton && /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(LegacyRequestChangesCommentButton, {
            approvalType: approval.meta.approvalType,
            onClickRequestChanges: openComments,
            disabled: hasUnrecoverableError,
            disabledReason: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "productApprovals.ApprovalEditorButtons.approvalsUnavailableTooltip"
            })
          })
        }, "legacy-request-changes"), menuOperations.map(operation => /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(ApprovalButton, {
            availableOperation: operation,
            disabled: !hasWritePermission || hasDisabledReason,
            disabledReason: approvalOperationDisabledReason,
            size: size
          })
        }, `approval-${operation}`)), showViewApprovalInMenu && /*#__PURE__*/_jsx(ListItem, {
          children: renderViewApproval()
        }, "view-approval"), additionalDropdownButtonItems]
      })
    }))]
  });
};
const ApprovalEditorButtonsWithContext = _ref2 => {
  let {
      children,
      showChildrenForRequestApproval = false
    } = _ref2,
    buttonGroupProps = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const outcome = useApprovalEditorButtonsOutcome({
    showChildrenForRequestApproval
  });
  if (outcome === 'children') {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  if (outcome === 'loading') {
    return /*#__PURE__*/_jsx(ApprovalEditorButtonSkeleton, {
      size: buttonGroupProps.size
    });
  }
  if (outcome === 'error') {
    return /*#__PURE__*/_jsx(ApprovalEditorErrorButton, {
      size: buttonGroupProps.size
    });
  }
  return /*#__PURE__*/_jsx(ApprovalEditorButtonGroup, Object.assign({}, buttonGroupProps));
};

/**
 * Requires the collaboration sidebar: `View approval` opens the approval panel
 * and the "Request changes" flows route through it, so the consuming app must
 * mount a CollaborationSidebarProvider configured for Approvals with
 * ApprovalsContent, plus a rendered AsyncExpandableCollaborationSidebar or
 * LayoutWithCollaborationSidebar. The component always drives the sidebar for
 * these actions — there is no way to opt out.
 *
 * @remarks
 * Consumer styling requirement: add the import below to the consuming app's
 * `project.sass`. Requesting an approval opens the approval modal's
 * hubspot-prosemirror note editor, which renders unstyled without it.
 *
 * @example
 * ```sass
 * @import hubspot-prosemirror/sass/project
 * ```
 */
export const ApprovalEditorButtons = props => {
  const approval = useContext(ApprovalContext);
  if (approval === undefined) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: props.children
    });
  }
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fingerprint: "ApprovalEditorButtons",
    children: /*#__PURE__*/_jsx(ApprovalEditorButtonsWithContext, Object.assign({}, props))
  });
};